import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/** How near the log's newest end a reader is at its foot, in px: one who is stays there as the log draws more. */
const FOOT_SLACK_PX = 48;
/** The height of the log is read in whole px, so a change of a px either way is rounding. */
const ROUNDING_PX = 1;

/** How far the reader is from the log's newest end: its bottom, or its top once reversed. */
const distanceFromNewestEnd = (scroller: HTMLElement, isReversed: boolean): number =>
  isReversed ? scroller.scrollTop : scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;

/**
 * Whether the handler is writing in a field inside the log: the page's focus is on one that takes text, such as a
 * question's answer, and not on a button or a link. It is read from the page's focus rather than from the focus
 * moving, which says the wrong thing twice: a field the log takes out of the page loses the focus with nothing said,
 * and one the browser blurs as the window loses its focus keeps it, and has it back with the window.
 */
const isWritingIn = (scroller: HTMLElement): boolean => {
  const { activeElement } = document;

  return activeElement !== null && scroller.contains(activeElement) && activeElement.matches(':read-write');
};

/**
 * Keeps a reader who is at the log's newest end there as the log draws more: an entry coming in or growing, Clive's
 * cursor appearing or going, or the view shrinking over the conversation's foot. A reader who scrolls away, or is taken
 * to an entry, is left where that takes them, and followed again once they scroll back to the newest end; a reversal
 * takes them there. A handler writing in a field inside the log is left where they are, the field with them, and their
 * place is read again as the log draws more: once they stop writing, where the log has left them decides whether it
 * follows them again, as a scroll would.
 *
 * Every scroll but the log's own is read, the browser's too: one that leaves the reader within the slack of the newest
 * end starts the log following, and one that leaves them past it stops it. The log's own scroll is told from the others
 * by where it left the log, until the next frame, by which time the browser has told the page of it: a scroll the
 * reader makes in that frame lands elsewhere, and is theirs. The log's scroll is instant, so there is no motion to
 * reduce. A scroll to an entry is instant too, so the browser tells the page of it in the frame's scroll steps, ahead
 * of any change in size that frame brings: the reader is read as away from the newest end before the entry they were
 * taken to is drawn at its own height.
 *
 * Returns the ref for the box that holds what the log draws, which is watched for a change in size; whether the reader
 * is at the newest end, which the stages rail reads; and whether the log keeps them there: they are at it, and not
 * writing in a field inside the log.
 */
export const useFollowNewest = (
  scrollerRef: RefObject<HTMLElement>,
  isReversed: boolean,
  /** The ids of the entries the log draws, in the order it draws them. */
  entryIds: string[],
  isCursorDrawn: boolean
): { contentRef: RefObject<HTMLDivElement>; isAtNewestEnd: () => boolean; shouldFollow: () => boolean } => {
  const contentRef = useRef<HTMLDivElement>(null);
  const isAtFootRef = useRef(true);
  /**
   * How far the reader was from the newest end when the log last read their place or put them there, or nearer, once a
   * change in size has brought the newest end nearer them.
   */
  const distanceRef = useRef(0);
  /** Where the log last scrolled itself to, until the next frame. */
  const pinnedAtRef = useRef<number | null>(null);
  const entriesKey = entryIds.join();

  const pin = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    scroller.scrollTop = isReversed ? 0 : scroller.scrollHeight;
    // As the browser held it, within the log.
    pinnedAtRef.current = scroller.scrollTop;
    distanceRef.current = distanceFromNewestEnd(scroller, isReversed);
    requestAnimationFrame(() => {
      pinnedAtRef.current = null;
    });
  }, [scrollerRef, isReversed]);

  /** Reads where the reader is: how far from the newest end, and whether that is within its slack. */
  const notePlace = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    distanceRef.current = distanceFromNewestEnd(scroller, isReversed);
    isAtFootRef.current = distanceRef.current <= FOOT_SLACK_PX;
  }, [scrollerRef, isReversed]);

  const isWriting = useCallback(() => scrollerRef.current !== null && isWritingIn(scrollerRef.current), [scrollerRef]);

  // Memoized: the stages rail reads the log's layout again whenever it changes.
  const isAtNewestEnd = useCallback(() => isAtFootRef.current, []);

  /** Whether the log keeps the reader at the newest end as it draws more: they are at it, and not writing. */
  const shouldFollow = useCallback(() => isAtFootRef.current && !isWriting(), [isWriting]);

  useLayoutEffect(() => {
    isAtFootRef.current = true;
  }, [isReversed]);

  useLayoutEffect(() => {
    if (shouldFollow()) pin();
    else if (isWriting()) notePlace();
  }, [entriesKey, isCursorDrawn, pin, shouldFollow, isWriting, notePlace]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const noteFoot = () => {
      if (scroller.scrollTop !== pinnedAtRef.current) notePlace();
    };
    scroller.addEventListener('scroll', noteFoot, { passive: true });

    return () => scroller.removeEventListener('scroll', noteFoot);
  }, [scrollerRef, notePlace]);

  // For the changes in size no entry or cursor makes: an entry growing, or the view shrinking over a longer queue. Only
  // a change that takes the newest end further from the reader moves them. One on the oldest side of the view (above
  // it, or below it once reversed), which the browser keeps the view still through, leaves them where they are: an
  // entry first drawn at its own height as they scroll away from the newest end. One that brings the newest end nearer
  // them, such as the view growing as the queue goes, is where the next is measured from.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return undefined;

    const observer = new ResizeObserver(() => {
      if (isWriting()) {
        notePlace();
        return;
      }

      const distance = distanceFromNewestEnd(scroller, isReversed);
      if (isAtFootRef.current && distance > distanceRef.current + ROUNDING_PX) pin();
      else if (distance < distanceRef.current - ROUNDING_PX) distanceRef.current = distance;
    });
    observer.observe(content);
    observer.observe(scroller);

    return () => observer.disconnect();
  }, [scrollerRef, isReversed, pin, isWriting, notePlace]);

  return { contentRef, isAtNewestEnd, shouldFollow };
};
